<template>
    <Head :title="`Checklist: ${checklist.checklist_number}`" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
            <div>
                <div class="flex items-center space-x-3">
                    <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">{{ checklist.template?.title }}</h1>
                    <span :class="getStatusClass(checklist.status)">
                        {{ getStatusLabel(checklist.status) }}
                    </span>
                </div>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {{ checklist.checklist_number }} • {{ checklist.location?.name || 'No location' }}
                </p>
            </div>
            <div class="flex items-center space-x-3">
                <Link
                    v-if="checklist.status === 'assigned' || checklist.status === 'in_progress'"
                    :href="route('checklists.conduct', checklist.id)"
                    class="inline-flex items-center px-4 py-2 bg-green-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-green-700"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    </svg>
                    {{ checklist.status === 'assigned' ? 'Start Checklist' : 'Continue' }}
                </Link>
                <Link
                    :href="route('checklists.index')"
                    class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                    Back to Checklists
                </Link>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <!-- Main Content -->
            <div class="lg:col-span-2 space-y-6">
                <!-- Summary -->
                <div v-if="checklist.status === 'completed' && (checklist.template?.has_scoring || checklist.has_failed_items)" class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Summary</h2>

                    <!-- Score (if scoring enabled) -->
                    <div v-if="checklist.template?.has_scoring && checklist.score !== null" class="space-y-2">
                        <div class="flex justify-between items-center">
                            <span class="text-sm text-gray-600 dark:text-gray-400">Score</span>
                            <span class="text-2xl font-bold" :class="checklist.score_percentage >= checklist.template.passing_score ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                                {{ checklist.score }} / {{ checklist.max_score }}
                            </span>
                        </div>
                        <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                            <div
                                class="bg-blue-600 h-3 rounded-full transition-all"
                                :style="{ width: `${checklist.score_percentage || 0}%` }"
                            ></div>
                        </div>
                        <p class="text-xs text-gray-500 dark:text-gray-400 text-center">
                            {{ checklist.score_percentage?.toFixed(1) }}% (Passing: {{ checklist.template.passing_score }}%)
                        </p>
                    </div>

                    <!-- Failed Items -->
                    <div v-if="checklist.has_failed_items" :class="checklist.template?.has_scoring ? 'mt-4 pt-4 border-t border-gray-200 dark:border-gray-700' : ''">
                        <div class="flex items-center space-x-2 text-red-600 dark:text-red-400">
                            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                            </svg>
                            <span class="text-sm font-medium">{{ checklist.failed_items_count }} Failed Items</span>
                        </div>
                    </div>
                </div>

                <!-- Template Preview (for assigned status) or Responses (for other statuses) -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">
                        {{ checklist.status === 'assigned' ? 'Checklist Fields' : 'Responses' }}
                    </h2>

                    <!-- Template Preview for assigned status -->
                    <div v-if="checklist.status === 'assigned'">
                        <StaticFieldsPreview
                            :sections="checklist.template?.sections || []"
                        />
                    </div>

                    <!-- Responses for all other statuses -->
                    <div v-else>
                        <div v-if="hasResponses" class="space-y-6">
                            <div v-for="(section, sIndex) in sectionsWithResponses" :key="section.id">
                                <h3 class="text-md font-medium text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                                    {{ sIndex + 1 }}. {{ section.title }}
                                </h3>

                                <div class="space-y-4">
                                    <div v-for="(field, fIndex) in fieldsWithResponses(section)" :key="field.id" class="pl-4 border-l-2" :class="getFieldBorderClass(field.id)">
                                        <!-- Question -->
                                        <div class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {{ sIndex + 1 }}.{{ fIndex + 1 }} {{ field.label }}
                                            <span v-if="field.is_required" class="text-red-500 ml-1">*</span>
                                        </div>

                                        <!-- Desired Answer (if available) -->
                                        <div v-if="getDesiredAnswerText(field.id)" class="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                            Desired Answer: {{ getDesiredAnswerText(field.id) }}
                                        </div>

                                        <!-- Response -->
                                        <div class="text-sm mb-2">
                                            <span class="text-gray-600 dark:text-gray-400 mr-2">Answer:</span>
                                            <span class="text-gray-900 dark:text-white font-medium">
                                                {{ getFieldResponse(field.id) }}
                                            </span>
                                        </div>

                                        <!-- Notes -->
                                        <div v-if="getFieldNotes(field.id)" class="mt-1 text-xs text-gray-600 dark:text-gray-400 italic">
                                            Notes: {{ getFieldNotes(field.id) }}
                                        </div>

                                        <!-- Supplement Photos -->
                                        <div v-if="getResponsePhotos(field.id).length > 0" class="mt-3">
                                            <p class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">📷 Photos:</p>
                                            <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                <div v-for="photo in getResponsePhotos(field.id)" :key="photo.id" class="relative group">
                                                    <a :href="photo.file_url" target="_blank" class="block">
                                                        <img
                                                            :src="photo.file_url"
                                                            :alt="photo.original_filename"
                                                            class="w-full h-24 object-cover rounded border border-gray-200 dark:border-gray-600 hover:opacity-75 transition-opacity"
                                                        />
                                                    </a>
                                                    <p class="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate" :title="photo.original_filename">
                                                        {{ photo.original_filename }}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Supplement Attachments -->
                                        <div v-if="getResponseAttachments(field.id).length > 0" class="mt-3">
                                            <p class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">📎 Attachments:</p>
                                            <div class="space-y-1">
                                                <a
                                                    v-for="attachment in getResponseAttachments(field.id)"
                                                    :key="attachment.id"
                                                    :href="attachment.file_url"
                                                    target="_blank"
                                                    class="flex items-center text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                                >
                                                    <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                                                    </svg>
                                                    {{ attachment.original_filename }}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center py-6 text-gray-500 dark:text-gray-400">
                            No responses yet
                        </div>
                    </div>
                </div>

                <!-- Comments -->
                <div v-if="checklist.comments && checklist.comments.length > 0" class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Comments</h2>
                    <div class="space-y-3">
                        <div v-for="comment in checklist.comments" :key="comment.id" class="border-l-2 border-blue-500 pl-3">
                            <div class="flex justify-between items-start">
                                <span class="text-sm font-medium text-gray-900 dark:text-white">
                                    {{ comment.user?.first_name }} {{ comment.user?.last_name }}
                                </span>
                                <span class="text-xs text-gray-500 dark:text-gray-400">
                                    {{ formatDate(comment.created_at) }}
                                </span>
                            </div>
                            <p class="text-sm text-gray-700 dark:text-gray-300 mt-1">{{ comment.comment }}</p>
                        </div>
                    </div>
                </div>

                <!-- Workflow Comments -->
                <div v-if="workflowLogs && workflowLogs.length > 0" class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Workflow Comments</h2>
                    <div class="space-y-4">
                        <div v-for="log in workflowLogs" :key="log.id" class="border-l-4 pl-4 py-2" :class="[
                            log.action_type === 'approval_approved' ? 'border-green-500' :
                            log.action_type === 'approval_rejected' ? 'border-red-500' :
                            'border-gray-300'
                        ]">
                            <div class="flex items-start justify-between">
                                <div class="flex items-center space-x-2">
                                    <span :class="[
                                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                                        log.action_type === 'approval_approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                        log.action_type === 'approval_rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                    ]">
                                        Level {{ log.approval_level }}
                                    </span>
                                    <span :class="[
                                        'text-xs font-semibold',
                                        log.action_type === 'approval_approved' ? 'text-green-600 dark:text-green-400' :
                                        log.action_type === 'approval_rejected' ? 'text-red-600 dark:text-red-400' :
                                        'text-gray-600 dark:text-gray-400'
                                    ]">
                                        {{ log.action_type === 'approval_approved' ? 'APPROVED' : log.action_type === 'approval_rejected' ? 'REJECTED' : log.action_type.toUpperCase() }}
                                    </span>
                                </div>
                                <span class="text-xs text-gray-500 dark:text-gray-400">
                                    {{ formatDate(log.created_at) }}
                                </span>
                            </div>
                            <p class="text-sm font-medium text-gray-900 dark:text-white mt-2">
                                {{ log.user?.first_name }} {{ log.user?.last_name }}
                            </p>
                            <p v-if="log.new_value?.comments" class="text-sm text-gray-700 dark:text-gray-300 mt-2 italic">
                                "{{ log.new_value.comments }}"
                            </p>
                            <p v-else class="text-sm text-gray-500 dark:text-gray-400 mt-2 italic">
                                No comments provided
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Activity Log -->
                <div v-if="auditLogs && auditLogs.length > 0" class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Activity Log</h2>
                    <div class="space-y-3">
                        <div v-for="log in auditLogs" :key="log.id" class="flex items-start space-x-3">
                            <div class="flex-shrink-0">
                                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="flex-1 min-w-0">
                                <p class="text-sm text-gray-900 dark:text-white">{{ log.description }}</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {{ log.user?.first_name }} {{ log.user?.last_name }} • {{ formatDate(log.created_at) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sidebar -->
            <div class="space-y-6">
                <!-- Details -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Details</h3>
                    <dl class="space-y-3">
                        <div>
                            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Template</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ checklist.template?.title }}</dd>
                        </div>
                        <div v-if="checklist.location">
                            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Location</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ checklist.location.name }}</dd>
                        </div>
                        <div v-if="checklist.assigned_to">
                            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Assigned To</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                                {{ checklist.assigned_to.first_name }} {{ checklist.assigned_to.last_name }}
                            </dd>
                        </div>
                        <div v-if="checklist.conducted_by">
                            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Conducted By</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                                {{ checklist.conducted_by.first_name }} {{ checklist.conducted_by.last_name }}
                            </dd>
                        </div>
                        <div v-if="checklist.due_date">
                            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Due Date</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatDate(checklist.due_date) }}</dd>
                        </div>
                        <div v-if="checklist.started_at">
                            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Started</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatDate(checklist.started_at) }}</dd>
                        </div>
                        <div v-if="checklist.conducted_at">
                            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Conducted</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatDate(checklist.conducted_at) }}</dd>
                        </div>
                        <div v-if="checklist.completed_at">
                            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Completed</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatDate(checklist.completed_at) }}</dd>
                        </div>
                        <div>
                            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Created</dt>
                            <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatDate(checklist.created_at) }}</dd>
                        </div>
                    </dl>
                </div>

                <!-- Approval Status -->
                <div v-if="checklist.approval_1_required || checklist.approval_2_required || checklist.approval_3_required" class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Approvals</h3>
                    <div class="space-y-3">
                        <!-- Approval 1 -->
                        <div v-if="checklist.approval_1_required" class="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                            <div class="flex items-start space-x-3">
                                <div :class="[
                                    'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center',
                                    checklist.approval_1_status === 'approved' ? 'bg-green-500' :
                                    checklist.approval_1_status === 'rejected' ? 'bg-red-500' :
                                    checklist.approval_1_status === 'pending' ? 'bg-blue-500' :
                                    'bg-gray-300 dark:bg-gray-600'
                                ]">
                                    <svg v-if="checklist.approval_1_status === 'approved'" class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                    </svg>
                                    <svg v-else-if="checklist.approval_1_status === 'rejected'" class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                    </svg>
                                    <span v-else class="text-xs text-white font-medium">1</span>
                                </div>
                                <div class="flex-1 min-w-0">
                                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ checklist.approval_1_step_name || 'Approval Level 1' }}</div>
                                    <div class="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                        {{ checklist.approval_1_status === null ? 'Locked' : checklist.approval_1_status || 'Pending' }}
                                    </div>
                                </div>
                            </div>
                            <div v-if="canApprove(1)" class="mt-3 flex space-x-2">
                                <button
                                    @click="openApprovalModal('approve', 1)"
                                    class="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded"
                                >
                                    Approve
                                </button>
                                <button
                                    @click="openApprovalModal('reject', 1)"
                                    class="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded"
                                >
                                    Reject
                                </button>
                            </div>
                        </div>

                        <!-- Approval 2 -->
                        <div v-if="checklist.approval_2_required" class="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                            <div class="flex items-start space-x-3">
                                <div :class="[
                                    'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center',
                                    checklist.approval_2_status === 'approved' ? 'bg-green-500' :
                                    checklist.approval_2_status === 'rejected' ? 'bg-red-500' :
                                    checklist.approval_2_status === 'pending' ? 'bg-blue-500' :
                                    'bg-gray-300 dark:bg-gray-600'
                                ]">
                                    <svg v-if="checklist.approval_2_status === 'approved'" class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                    </svg>
                                    <svg v-else-if="checklist.approval_2_status === 'rejected'" class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                    </svg>
                                    <span v-else class="text-xs text-white font-medium">2</span>
                                </div>
                                <div class="flex-1 min-w-0">
                                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ checklist.approval_2_step_name || 'Approval Level 2' }}</div>
                                    <div class="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                        {{ checklist.approval_2_status === null ? 'Locked' : checklist.approval_2_status || 'Pending' }}
                                    </div>
                                </div>
                            </div>
                            <div v-if="canApprove(2)" class="mt-3 flex space-x-2">
                                <button
                                    @click="openApprovalModal('approve', 2)"
                                    class="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded"
                                >
                                    Approve
                                </button>
                                <button
                                    @click="openApprovalModal('reject', 2)"
                                    class="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded"
                                >
                                    Reject
                                </button>
                            </div>
                        </div>

                        <!-- Approval 3 -->
                        <div v-if="checklist.approval_3_required" class="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                            <div class="flex items-start space-x-3">
                                <div :class="[
                                    'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center',
                                    checklist.approval_3_status === 'approved' ? 'bg-green-500' :
                                    checklist.approval_3_status === 'rejected' ? 'bg-red-500' :
                                    checklist.approval_3_status === 'pending' ? 'bg-blue-500' :
                                    'bg-gray-300 dark:bg-gray-600'
                                ]">
                                    <svg v-if="checklist.approval_3_status === 'approved'" class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                    </svg>
                                    <svg v-else-if="checklist.approval_3_status === 'rejected'" class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                    </svg>
                                    <span v-else class="text-xs text-white font-medium">3</span>
                                </div>
                                <div class="flex-1 min-w-0">
                                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ checklist.approval_3_step_name || 'Approval Level 3' }}</div>
                                    <div class="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                        {{ checklist.approval_3_status === null ? 'Locked' : checklist.approval_3_status || 'Pending' }}
                                    </div>
                                </div>
                            </div>
                            <div v-if="canApprove(3)" class="mt-3 flex space-x-2">
                                <button
                                    @click="openApprovalModal('approve', 3)"
                                    class="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded"
                                >
                                    Approve
                                </button>
                                <button
                                    @click="openApprovalModal('reject', 3)"
                                    class="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded"
                                >
                                    Reject
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Approval Modal -->
        <div v-if="showApprovalModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <!-- Background overlay -->
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" @click="closeApprovalModal"></div>

                <!-- Center modal -->
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div class="relative z-10 inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div class="sm:flex sm:items-start">
                            <div :class="[
                                'mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10',
                                approvalAction === 'approve' ? 'bg-green-100' : 'bg-red-100'
                            ]">
                                <svg v-if="approvalAction === 'approve'" class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                </svg>
                                <svg v-else class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                                    {{ approvalAction === 'approve' ? 'Approve' : 'Reject' }} {{ checklist[`approval_${approvalLevel}_step_name`] || `Approval Level ${approvalLevel}` }}
                                </h3>
                                <div class="mt-4">
                                    <label for="comments" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Comments {{ approvalAction === 'reject' ? '(Required)' : '(Optional)' }}
                                    </label>
                                    <textarea
                                        id="comments"
                                        v-model="approvalComments"
                                        rows="4"
                                        class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                        :placeholder="approvalAction === 'approve' ? 'Add any comments about this approval...' : 'Please provide a reason for rejection...'"
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            @click="submitApproval"
                            :disabled="isSubmittingApproval"
                            :class="[
                                'w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm',
                                approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
                                isSubmittingApproval ? 'opacity-50 cursor-not-allowed' : ''
                            ]"
                        >
                            {{ isSubmittingApproval ? 'Processing...' : (approvalAction === 'approve' ? 'Approve' : 'Reject') }}
                        </button>
                        <button
                            type="button"
                            @click="closeApprovalModal"
                            :disabled="isSubmittingApproval"
                            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Head, Link, router, usePage } from '@inertiajs/vue3';
import { useTimezone } from '@/Composables/useTimezone';
import route from '@/Utils/route';
import StaticFieldsPreview from '@/Components/Checklists/StaticFieldsPreview.vue';

const props = defineProps({
    checklist: Object,
    workflowLogs: {
        type: Array,
        default: () => [],
    },
    auditLogs: {
        type: Array,
        default: () => [],
    },
    companyOwner: {
        type: Object,
        default: null,
    },
});

const page = usePage();
const currentUser = computed(() => page.props.auth?.user);
// Company context protection - get current company_id
const companyId = computed(() => page.props.company_id);

// Approval modal state
const showApprovalModal = ref(false);
const approvalAction = ref(null); // 'approve' or 'reject'
const approvalLevel = ref(null); // 1, 2, or 3
const approvalComments = ref('');
const isSubmittingApproval = ref(false);

const { formatDateTime: formatDateTimeInTZ } = useTimezone();

// Check if there are any responses
const hasResponses = computed(() => {
    return props.checklist.responses && props.checklist.responses.length > 0;
});

// Get sections that have at least one field with a response
const sectionsWithResponses = computed(() => {
    if (!props.checklist.template?.sections) return [];

    const sections = [...props.checklist.template.sections]
        .sort((a, b) => a.display_order - b.display_order)
        .filter(section => {
            const fields = section.fields || [];
            return fields.some(field => hasFieldResponse(field.id));
        });

    return sections;
});

// Get fields in a section that have responses
const fieldsWithResponses = (section) => {
    if (!section.fields) return [];

    return [...section.fields]
        .sort((a, b) => a.display_order - b.display_order)
        .filter(field => hasFieldResponse(field.id));
};

// Check if a field has a response
const hasFieldResponse = (fieldId) => {
    return props.checklist.responses?.some(r => r.template_field_id === fieldId);
};

// Get response object for a field
const getResponseObject = (fieldId) => {
    return props.checklist.responses?.find(r => r.template_field_id === fieldId);
};

// Get formatted field response based on field type
const getFieldResponse = (fieldId) => {
    const response = getResponseObject(fieldId);
    if (!response) return 'No response';

    // Determine which response column to use based on field type
    switch (response.field_type) {
        // Text fields
        case 'text_input':
        case 'textarea':
        case 'dropdown_select':
        case 'multiple_choice':
            return response.response_text || 'No response';

        // Multiple Selection (Checkboxes) - parse JSON array
        case 'multiple_selection':
            try {
                const values = JSON.parse(response.response_text);
                return Array.isArray(values) ? values.join(', ') : response.response_text;
            } catch {
                return response.response_text || 'No response';
            }

        // Yes/No field
        case 'yes_no':
            return response.response_text || 'No response';

        // Pass/Fail/NA field
        case 'pass_fail_na':
            if (response.is_failed === 2) return 'Pass';
            if (response.is_failed === 1) return 'Fail';
            if (response.is_failed === 0) return 'N/A';
            return response.response_text || 'No response';

        // Numeric fields
        case 'number_input':
        case 'decimal_input':
        case 'rating_scale':
            return response.response_numeric !== null ? response.response_numeric.toString() : 'No response';

        // Date/Time fields
        case 'date_picker':
        case 'time_picker':
        case 'datetime_picker':
            return response.response_date ? formatDate(response.response_date) : 'No response';

        // Special fields
        case 'signature':
            return response.response_text ? 'Signed' : 'No signature';

        case 'photo_capture':
            // For photo_capture fields, the photos are displayed visually below
            // So we just return empty string since the visual display is the answer
            return '';

        case 'file_upload':
            // For file_upload fields, the files are displayed visually below
            // So we just return empty string since the visual display is the answer
            return '';

        default:
            return response.response_text || response.response_numeric?.toString() || 'No response';
    }
};

// Get desired answer text in human-readable format
const getDesiredAnswerText = (fieldId) => {
    const response = getResponseObject(fieldId);
    if (!response || !response.desired_answer) return null;

    try {
        const desiredAnswer = typeof response.desired_answer === 'string'
            ? JSON.parse(response.desired_answer)
            : response.desired_answer;

        if (!desiredAnswer || !desiredAnswer.type) return null;

        switch (desiredAnswer.type) {
            case 'exact_value':
                return desiredAnswer.value;

            case 'any_of':
                return desiredAnswer.any_of?.join(' or ');

            case 'none_of':
                return `Not: ${desiredAnswer.none_of?.join(', ')}`;

            case 'all_selected':
                return `All of: ${desiredAnswer.all_options?.join(', ')}`;

            case 'range':
                return `${desiredAnswer.min} - ${desiredAnswer.max}`;

            case 'greater_than':
                return `> ${desiredAnswer.value}`;

            case 'greater_than_equal':
                return `≥ ${desiredAnswer.value}`;

            case 'less_than':
                return `< ${desiredAnswer.value}`;

            case 'less_than_equal':
                return `≤ ${desiredAnswer.value}`;

            case 'before_submission_date':
                return 'Before submission date';

            case 'on_and_before_submission_date':
                return 'On or before submission date';

            case 'after_submission_date':
                return 'After submission date';

            case 'on_and_after_submission_date':
                return 'On or after submission date';

            case 'any':
            case 'always_pass':
                return 'Any answer';

            default:
                return null;
        }
    } catch {
        return null;
    }
};

// Get field notes
const getFieldNotes = (fieldId) => {
    const response = getResponseObject(fieldId);
    return response?.notes || null;
};

// Get photos count
const getResponsePhotosCount = (fieldId) => {
    const response = getResponseObject(fieldId);
    return response?.photos_count || 0;
};

// Get attachments count
const getResponseAttachmentsCount = (fieldId) => {
    const response = getResponseObject(fieldId);
    return response?.attachments_count || 0;
};

// Get photos array (supplement photos with attachment_type = 'photo')
const getResponsePhotos = (fieldId) => {
    const response = getResponseObject(fieldId);
    return response?.photos || [];
};

// Get attachments array (supplement files with attachment_type = 'file')
const getResponseAttachments = (fieldId) => {
    const response = getResponseObject(fieldId);
    if (!response?.attachments) return [];
    // Filter to only get file type attachments (not photos)
    return response.attachments.filter(att => att.attachment_type === 'file');
};

// Get border color based on pass/fail status
const getFieldBorderClass = (fieldId) => {
    const response = getResponseObject(fieldId);
    if (!response) return 'border-gray-200 dark:border-gray-700';

    // is_failed: 0 = NA, 1 = Fail, 2 = Pass
    if (response.is_failed === 2) {
        return 'border-green-500 dark:border-green-500';
    } else if (response.is_failed === 1) {
        return 'border-red-500 dark:border-red-500';
    }

    return 'border-gray-200 dark:border-gray-700';
};

const getStatusClass = (status) => {
    const classes = {
        assigned: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        in_progress: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        pending_review: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        under_review: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
        pending_approval: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        rejected: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
        completed: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        cancelled: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
    };
    return classes[status] || classes.assigned;
};

const getStatusLabel = (status) => {
    const labels = {
        assigned: 'Assigned',
        in_progress: 'In Progress',
        pending_review: 'Pending Review',
        under_review: 'Under Review',
        pending_approval: 'Pending Approval',
        rejected: 'Rejected',
        completed: 'Completed',
        cancelled: 'Cancelled',
    };
    return labels[status] || status;
};

// Check if current user can approve a specific level
const canApprove = (level) => {
    if (!currentUser.value) return false;
    const assignedUserId = props.checklist[`approval_${level}_user_id`];
    const status = props.checklist[`approval_${level}_status`];
    const required = props.checklist[`approval_${level}_required`];

    // Use loose equality (==) to handle type differences (string vs number)
    // eslint-disable-next-line eqeqeq
    return required && assignedUserId == currentUser.value.id && status === 'pending';
};

// Open approval modal
const openApprovalModal = (action, level) => {
    approvalAction.value = action;
    approvalLevel.value = level;
    approvalComments.value = '';
    showApprovalModal.value = true;
};

// Close approval modal
const closeApprovalModal = () => {
    showApprovalModal.value = false;
    approvalAction.value = null;
    approvalLevel.value = null;
    approvalComments.value = '';
};

// Submit approval/rejection
const submitApproval = () => {
    if (approvalAction.value === 'reject' && !approvalComments.value.trim()) {
        alert('Please provide comments for rejection.');
        return;
    }

    // Validate company context is available (for cross-tenant protection)
    if (!companyId.value) {
        alert('Company context is missing. Please reload the page and try again.');
        return;
    }

    isSubmittingApproval.value = true;

    const endpoint = approvalAction.value === 'approve'
        ? route('checklists.workflow.approve', props.checklist.id)
        : route('checklists.workflow.reject', props.checklist.id);

    router.post(endpoint, {
        approval_level: approvalLevel.value,
        comments: approvalComments.value || null,
        _company_id: companyId.value, // Include company context for validation
    }, {
        preserveScroll: true,
        onSuccess: () => {
            closeApprovalModal();
        },
        onError: (errors) => {
            console.error('Approval error:', errors);
        },
        onFinish: () => {
            isSubmittingApproval.value = false;
        },
    });
};

const formatDate = (date) => {
    if (!date) return 'N/A';
    return formatDateTimeInTZ(date);
};
</script>
